import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  AlertTriangle, Check, Flag, Pencil, RefreshCw, Search, ShieldAlert, Trash2,
  Users, Shield, Calendar, Layers, BarChart3, X, Plus,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

/* ────────────────────── shared bits ────────────────────── */
function StatTile({ label, value, sub, accent }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        accent
          ? "border-[var(--accent)]/40 bg-[var(--accent)]/8"
          : "border-white/10 bg-[var(--surface)]"
      }`}
      data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-3xl font-black tabular-nums mt-1">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function Modal({ open, onClose, title, children, testId, width = "max-w-xl" }) {
  if (!open) return null;
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm grid place-items-center p-4 z-50"
        onClick={onClose}
        data-testid={testId}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className={`bg-[var(--surface)] border border-white/10 rounded-2xl shadow-2xl ${width} w-full p-6 max-h-[90vh] overflow-auto`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">{title}</h3>
            <button onClick={onClose} className="text-zinc-400 hover:text-white" aria-label="Close">
              <X size={18} />
            </button>
          </div>
          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Pager({ page, pageSize, total, onPage }) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between text-xs text-zinc-500">
      <span>{total.toLocaleString()} rows · page {page} / {pages}</span>
      <div className="flex gap-1">
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          className="px-3 py-1 rounded-md border border-white/10 disabled:opacity-40"
        >
          prev
        </button>
        <button
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          className="px-3 py-1 rounded-md border border-white/10 disabled:opacity-40"
        >
          next
        </button>
      </div>
    </div>
  );
}

/* ────────────────────── Overview ────────────────────── */
function Overview() {
  const [data, setData] = useState(null);
  const [regenOpen, setRegenOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [auditing, setAuditing] = useState(false);
  const [auditResult, setAuditResult] = useState(null);
  const [regen, setRegen] = useState({ mode: "classic", difficulty: "medium", count: 1 });
  const [regenBusy, setRegenBusy] = useState(false);

  const reload = useCallback(async () => {
    const { data } = await api.get("/admin/overview");
    setData(data);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const onRegen = async () => {
    setRegenBusy(true);
    try {
      const { data } = await api.post("/admin/puzzles/regenerate", regen);
      toast.success(`Created ${data.created}/${data.requested} puzzles`);
      setRegenOpen(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally { setRegenBusy(false); }
  };

  const onAudit = async () => {
    setAuditing(true);
    try {
      const { data } = await api.post("/admin/puzzles/audit");
      setAuditResult(data);
    } catch (e) {
      toast.error("Audit failed");
    } finally { setAuditing(false); }
  };

  if (!data) return <div className="text-zinc-500">loading…</div>;
  const bm = data.puzzles_by_difficulty?.by_mode || {};
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Users" value={data.users} />
        <StatTile label="Players" value={data.players} sub={`${data.deleted_players} soft-deleted`} />
        <StatTile label="Teams" value={data.teams} sub={`${data.deleted_teams} soft-deleted`} />
        <StatTile label="Stints" value={data.stints} />
        <StatTile label="Edges" value={data.edges.toLocaleString()} />
        <StatTile label="Flagged" value={data.flagged_puzzles} accent={data.flagged_puzzles > 0} />
        <StatTile label="Active lobbies" value={data.lobbies.active} />
        <StatTile label="Waiting lobbies" value={data.lobbies.waiting} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Classic puzzles</div>
          <div className="flex gap-4">
            {["medium","hard","insane"].map(d => (
              <div key={d}><div className="text-[10px] uppercase text-zinc-500">{d}</div>
                <div className="text-2xl font-black tabular-nums">{bm.classic?.[d] ?? 0}</div></div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-white/10 bg-[var(--surface)] p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Career Path puzzles</div>
          <div className="flex gap-4">
            {["medium","hard","insane"].map(d => (
              <div key={d}><div className="text-[10px] uppercase text-zinc-500">{d}</div>
                <div className="text-2xl font-black tabular-nums">{bm.career_path?.[d] ?? 0}</div></div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setRegenOpen(true)}
          data-testid="cta-regenerate"
          className="px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold hover:brightness-110 transition inline-flex items-center gap-2"
        >
          <Plus size={14} /> Regenerate puzzles
        </button>
        <button
          onClick={() => { setAuditOpen(true); setAuditResult(null); }}
          data-testid="cta-audit"
          className="px-4 py-2 rounded-full border border-white/15 hover:bg-white/5 transition inline-flex items-center gap-2"
        >
          <ShieldAlert size={14} /> Run pool audit
        </button>
      </div>

      <Modal open={regenOpen} onClose={() => setRegenOpen(false)} title="Regenerate puzzles" testId="regen-modal">
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <label className="text-xs">Mode
              <select className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5"
                value={regen.mode} onChange={(e) => setRegen({ ...regen, mode: e.target.value })}>
                <option value="classic">Classic</option>
                <option value="career_path">Career Path</option>
              </select>
            </label>
            <label className="text-xs">Difficulty
              <select className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5"
                value={regen.difficulty} onChange={(e) => setRegen({ ...regen, difficulty: e.target.value })}>
                <option>medium</option><option>hard</option><option>insane</option>
              </select>
            </label>
            <label className="text-xs">Count
              <input type="number" min={1} max={50} className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5"
                value={regen.count} onChange={(e) => setRegen({ ...regen, count: Number(e.target.value) })} />
            </label>
          </div>
          <button onClick={onRegen} disabled={regenBusy} data-testid="regen-submit"
            className="w-full px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold disabled:opacity-50">
            {regenBusy ? "generating…" : `Generate ${regen.count}`}
          </button>
        </div>
      </Modal>

      <Modal open={auditOpen} onClose={() => setAuditOpen(false)} title="Pool audit" testId="audit-modal" width="max-w-2xl">
        {!auditResult ? (
          <button onClick={onAudit} disabled={auditing}
            data-testid="audit-run"
            className="w-full px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold disabled:opacity-50">
            {auditing ? "auditing…" : "Run audit on full pool"}
          </button>
        ) : (
          <div className="space-y-3" data-testid="audit-result">
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-md border border-white/10 p-3">
                <div className="text-[10px] uppercase text-zinc-500">Total</div>
                <div className="text-2xl font-black">{auditResult.total}</div>
              </div>
              <div className="rounded-md border border-[var(--accent)]/30 bg-[var(--accent)]/10 p-3">
                <div className="text-[10px] uppercase text-[var(--accent)]">Valid</div>
                <div className="text-2xl font-black text-[var(--accent)]">{auditResult.valid}</div>
              </div>
              <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3">
                <div className="text-[10px] uppercase text-red-300">Invalid/Ambiguous</div>
                <div className="text-2xl font-black text-red-300">
                  {auditResult.invalid.length + auditResult.ambiguous.length}
                </div>
              </div>
            </div>
            {auditResult.ambiguous.length > 0 && (
              <div className="text-xs">
                <div className="font-bold mb-1">Ambiguous puzzles</div>
                <ul className="space-y-1">
                  {auditResult.ambiguous.slice(0, 10).map((a) => (
                    <li key={a.id} className="text-zinc-400">
                      {a.id.slice(0, 8)} — intersects {a.intersection_size}: {a.intersection_names.join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ────────────────────── Players ────────────────────── */
function PlayersTab() {
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 50 });
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/players?q=${encodeURIComponent(q)}&page=${page}&page_size=25`);
    setData(data);
  }, [q, page]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/players/${id}`);
    setSelected(data);
    setEditing({
      name: data.name, nationality: data.nationality || "",
      position: data.position || "", image_url: data.image_url || "",
      era_tag: data.era_tag || "",
    });
  };
  const save = async () => {
    try {
      const { data } = await api.patch(`/admin/players/${selected.id}`, editing);
      toast.success("Saved");
      setSelected(data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };
  const onDelete = async () => {
    if (!confirm("Soft-delete this player? They won't appear in new puzzles.")) return;
    await api.delete(`/admin/players/${selected.id}`);
    toast.success("Soft-deleted");
    setSelected(null);
    load();
  };

  return (
    <div className="space-y-4" data-testid="admin-players">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-3 text-zinc-500" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="search by name…" data-testid="players-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-[var(--surface)] border border-white/10 focus:border-[var(--accent)] outline-none text-sm" />
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[var(--surface)]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[var(--surface-2)] border-b border-white/10">
            <tr><th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Name</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Nationality</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Pos</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Fame</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">Stints</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} onClick={() => openDetail(p.id)}
                data-testid={`player-row-${p.id}`}
                className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${p.deleted ? "opacity-50" : ""}`}>
                <td className="px-3 py-2 font-medium">{p.name}{p.deleted && <span className="text-[9px] ml-2 text-red-300">DEL</span>}</td>
                <td className="px-3 py-2 text-zinc-400">{p.nationality}</td>
                <td className="px-3 py-2 text-zinc-400">{p.position}</td>
                <td className="px-3 py-2 text-right text-zinc-400 tabular-nums">{p.fame_score}</td>
                <td className="px-3 py-2 text-right text-zinc-400 tabular-nums">{p.stint_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={data.page_size} total={data.total} onPage={setPage} />

      <Modal open={!!selected} onClose={() => setSelected(null)} title={selected?.name} testId="player-detail" width="max-w-2xl">
        {selected && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3 text-sm">
              {["name","nationality","position","image_url","era_tag"].map((k) => (
                <label key={k} className="text-xs">{k}
                  <input value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })}
                    data-testid={`player-edit-${k}`}
                    className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5 focus:border-[var(--accent)] outline-none" />
                </label>
              ))}
            </div>
            <div className="text-xs text-zinc-500">
              {selected.stints?.length || 0} stints · {selected.edges_count} teammate edges
            </div>
            <div className="text-[11px] max-h-40 overflow-y-auto rounded-md border border-white/5 p-2 bg-black/30">
              {(selected.stints || []).map((s) => (
                <div key={s.id} className="flex justify-between"><span>{s.team?.name}</span><span className="text-zinc-500 font-mono">{s.start_year}-{s.end_year}</span></div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={save} data-testid="player-save"
                className="px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold">Save</button>
              <button onClick={onDelete} data-testid="player-delete"
                className="px-4 py-2 rounded-full border border-red-500/30 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1"><Trash2 size={12} /> Soft-delete</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

/* ────────────────────── Puzzles ────────────────────── */
function PuzzlesTab() {
  const [filters, setFilters] = useState({ mode: "", difficulty: "", flagged: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 25 });
  const [selected, setSelected] = useState(null);
  const [flagOpen, setFlagOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");

  const load = useCallback(async () => {
    const qs = new URLSearchParams();
    if (filters.mode) qs.set("mode", filters.mode);
    if (filters.difficulty) qs.set("difficulty", filters.difficulty);
    if (filters.flagged === "true") qs.set("flagged", "true");
    if (filters.flagged === "false") qs.set("flagged", "false");
    qs.set("page", page); qs.set("page_size", "25");
    const { data } = await api.get(`/admin/puzzles?${qs.toString()}`);
    setData(data);
  }, [filters, page]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/puzzles/${id}`);
    setSelected(data);
  };
  const flag = async () => {
    if (!flagReason.trim()) return;
    await api.post(`/admin/puzzles/${selected.id}/flag`, { reason: flagReason });
    toast.success("Flagged");
    setFlagOpen(false); setFlagReason("");
    openDetail(selected.id); load();
  };
  const unflag = async () => {
    await api.post(`/admin/puzzles/${selected.id}/unflag`);
    toast.success("Unflagged");
    openDetail(selected.id); load();
  };

  return (
    <div className="space-y-4" data-testid="admin-puzzles">
      <div className="flex gap-2 flex-wrap">
        {["mode:classic","mode:career_path"].map(opt => {
          const [, val] = opt.split(":");
          const active = filters.mode === val;
          return <button key={opt} onClick={() => setFilters(f => ({ ...f, mode: active ? "" : val }))}
            data-testid={`filter-mode-${val}`}
            className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${active ? "bg-[var(--accent)] text-black border-[var(--accent)]" : "border-white/10 text-zinc-300"}`}>{val.replace("_"," ")}</button>;
        })}
        {["medium","hard","insane"].map(d => {
          const active = filters.difficulty === d;
          return <button key={d} onClick={() => setFilters(f => ({ ...f, difficulty: active ? "" : d }))}
            className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${active ? "bg-[var(--accent)] text-black border-[var(--accent)]" : "border-white/10 text-zinc-300"}`}>{d}</button>;
        })}
        <button onClick={() => setFilters(f => ({ ...f, flagged: f.flagged === "true" ? "" : "true" }))}
          data-testid="filter-flagged"
          className={`text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border ${filters.flagged === "true" ? "bg-red-500 text-white border-red-500" : "border-white/10 text-zinc-300"}`}>flagged only</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[var(--surface)]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[var(--surface-2)] border-b border-white/10">
            <tr><th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Correct player</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Mode</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Diff</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Score</th>
              <th className="text-center px-3 py-2 text-[10px] uppercase text-zinc-500">Flagged</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((p) => (
              <tr key={p.id} onClick={() => openDetail(p.id)}
                data-testid={`puzzle-row-${p.id}`}
                className={`border-b border-white/5 hover:bg-white/5 cursor-pointer ${p.flagged ? "bg-red-500/5" : ""}`}>
                <td className="px-3 py-2 font-medium">{p.correct_player?.name}</td>
                <td className="px-3 py-2 text-zinc-400">{p.mode.replace("_"," ")}</td>
                <td className="px-3 py-2 text-zinc-400">{p.difficulty}</td>
                <td className="px-3 py-2 text-right text-zinc-400 tabular-nums">{Math.round(p.difficulty_score || 0)}</td>
                <td className="px-3 py-2 text-center">{p.flagged ? <Flag size={12} className="text-red-400 inline" /> : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager page={page} pageSize={data.page_size} total={data.total} onPage={setPage} />

      <Modal open={!!selected} onClose={() => setSelected(null)} title={`${selected?.mode?.replace("_"," ")} · ${selected?.correct_player?.name}`} testId="puzzle-detail" width="max-w-2xl">
        {selected && (
          <div className="space-y-4 text-sm">
            <div className={`text-xs px-3 py-2 rounded-md border ${selected.validation?.valid ? "border-[var(--accent)]/30 bg-[var(--accent)]/10 text-[var(--accent)]" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>
              validation: {selected.validation?.valid ? "valid" : "INVALID"}
              {selected.validation?.intersection && ` · intersection: ${selected.validation.intersection.join(", ")}`}
            </div>
            {selected.mode === "classic" ? (
              <ol className="space-y-1 text-xs">
                {(selected.cards_summary || []).map((c, i) => (
                  <li key={i} className="flex justify-between"><span>{c.team_name}</span><span className="font-mono text-zinc-500">{c.years[0]}-{c.years[1]}</span></li>
                ))}
              </ol>
            ) : (
              <ol className="space-y-1 text-xs">
                {(selected.career_path || []).map((s, i) => (
                  <li key={i} className="flex justify-between"><span>{s.team_name} <span className="text-zinc-600">({s.team_type})</span></span><span className="font-mono text-zinc-500">{s.start_year}-{s.end_year}</span></li>
                ))}
              </ol>
            )}
            <div className="flex gap-2 flex-wrap">
              {selected.flagged ? (
                <button onClick={unflag} data-testid="puzzle-unflag"
                  className="px-3 py-1.5 rounded-full border border-white/15 text-xs">Unflag</button>
              ) : (
                <button onClick={() => setFlagOpen(true)} data-testid="puzzle-flag-btn"
                  className="px-3 py-1.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-300 text-xs inline-flex items-center gap-1"><Flag size={12} /> Flag</button>
              )}
              <button onClick={async () => { await api.delete(`/admin/puzzles/${selected.id}`); toast.success("Deleted"); setSelected(null); load(); }}
                className="px-3 py-1.5 rounded-full border border-red-500/30 text-red-300 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Delete</button>
              <button onClick={async () => {
                const { data } = await api.post("/admin/puzzles/regenerate", { mode: selected.mode, difficulty: selected.difficulty, count: 1 });
                toast.success(`Created ${data.created} new puzzle`);
              }}
                className="px-3 py-1.5 rounded-full border border-[var(--accent)]/30 text-[var(--accent)] text-xs inline-flex items-center gap-1">
                <RefreshCw size={12} /> Regenerate one
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={flagOpen} onClose={() => setFlagOpen(false)} title="Flag puzzle" testId="flag-modal">
        <div className="space-y-3">
          <label className="text-xs">Reason
            <textarea value={flagReason} onChange={(e) => setFlagReason(e.target.value)}
              data-testid="flag-reason"
              maxLength={300} rows={3}
              className="mt-1 w-full bg-black border border-white/10 rounded-md px-3 py-2 focus:border-[var(--accent)] outline-none" />
          </label>
          <button onClick={flag} disabled={!flagReason.trim()}
            data-testid="flag-submit"
            className="w-full px-4 py-2 rounded-full bg-red-500 text-white font-bold disabled:opacity-40">Flag puzzle</button>
        </div>
      </Modal>
    </div>
  );
}

/* ────────────────────── Stints ────────────────────── */
function StintsTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ player_q: "", team_q: "", player: null, team: null, start_year: 2020, end_year: 2024 });
  const [pSugg, setPSugg] = useState([]); const [tSugg, setTSugg] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/stints?page=1&page_size=50");
    setItems(data.items); setTotal(data.total);
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!form.player_q) return setPSugg([]);
    const t = setTimeout(async () => {
      const { data } = await api.get(`/players/search?q=${encodeURIComponent(form.player_q)}&limit=5`);
      setPSugg(data);
    }, 200); return () => clearTimeout(t);
  }, [form.player_q]);
  useEffect(() => {
    if (!form.team_q) return setTSugg([]);
    const t = setTimeout(async () => {
      const { data } = await api.get(`/teams/search?q=${encodeURIComponent(form.team_q)}&limit=5`);
      setTSugg(data);
    }, 200); return () => clearTimeout(t);
  }, [form.team_q]);

  const submit = async () => {
    if (!form.player || !form.team) return toast.error("Pick player + team");
    await api.post("/admin/stints", {
      player_id: form.player._id, team_id: form.team._id,
      start_year: Number(form.start_year), end_year: Number(form.end_year),
    });
    toast.success("Stint added, edges rebuilding…");
    setAddOpen(false);
    setForm({ player_q: "", team_q: "", player: null, team: null, start_year: 2020, end_year: 2024 });
    setTimeout(load, 1500);
  };

  return (
    <div className="space-y-4" data-testid="admin-stints">
      <div className="flex justify-between items-center">
        <div className="text-xs text-zinc-500">{total.toLocaleString()} stints (showing first 50)</div>
        <button onClick={() => setAddOpen(true)} data-testid="stint-add-btn"
          className="px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold inline-flex items-center gap-2"><Plus size={14} /> Add stint</button>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[var(--surface)]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[var(--surface-2)] border-b border-white/10">
            <tr><th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Player</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Team</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Years</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Source</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{s.player_id.slice(0,8)}</td>
                <td className="px-3 py-2 text-zinc-400 font-mono text-xs">{s.team_id.slice(0,8)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{s.start_year}-{s.end_year}</td>
                <td className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500">{s.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add stint" testId="add-stint-modal">
        <div className="space-y-3">
          <div className="relative">
            <input value={form.player_q} placeholder={form.player ? form.player.name : "Search player…"}
              onChange={(e) => setForm({ ...form, player_q: e.target.value, player: null })}
              data-testid="add-stint-player"
              className="w-full px-3 py-2 rounded-md bg-black border border-white/10 outline-none text-sm" />
            {pSugg.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-md max-h-40 overflow-auto">
                {pSugg.map((p) => (
                  <button key={p._id} type="button" onClick={() => setForm({ ...form, player: p, player_q: p.name })}
                    className="w-full text-left px-3 py-1.5 hover:bg-white/5 text-sm">{p.name}</button>
                ))}
              </div>
            )}
          </div>
          <div className="relative">
            <input value={form.team_q} placeholder={form.team ? form.team.name : "Search team…"}
              onChange={(e) => setForm({ ...form, team_q: e.target.value, team: null })}
              data-testid="add-stint-team"
              className="w-full px-3 py-2 rounded-md bg-black border border-white/10 outline-none text-sm" />
            {tSugg.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-[var(--surface-2)] border border-white/10 rounded-md max-h-40 overflow-auto">
                {tSugg.map((t) => (
                  <button key={t._id} type="button" onClick={() => setForm({ ...form, team: t, team_q: t.name })}
                    className="w-full text-left px-3 py-1.5 hover:bg-white/5 text-sm">{t.name}</button>
                ))}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-xs">Start
              <input type="number" min={1900} max={2100} value={form.start_year}
                onChange={(e) => setForm({ ...form, start_year: e.target.value })}
                data-testid="add-stint-start"
                className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5" /></label>
            <label className="text-xs">End
              <input type="number" min={1900} max={2100} value={form.end_year}
                onChange={(e) => setForm({ ...form, end_year: e.target.value })}
                data-testid="add-stint-end"
                className="mt-1 w-full bg-black border border-white/10 rounded-md px-2 py-1.5" /></label>
          </div>
          <button onClick={submit} data-testid="add-stint-submit"
            className="w-full px-4 py-2 rounded-full bg-[var(--accent)] text-black font-bold">Add stint</button>
        </div>
      </Modal>
    </div>
  );
}

/* ────────────────────── Teams ────────────────────── */
function TeamsTab() {
  const [q, setQ] = useState("");
  const [data, setData] = useState({ items: [], total: 0, page_size: 50 });
  useEffect(() => {
    api.get(`/admin/teams?q=${encodeURIComponent(q)}&page_size=50`).then(({ data }) => setData(data));
  }, [q]);
  return (
    <div className="space-y-4" data-testid="admin-teams">
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="search teams…"
        className="px-3 py-2 rounded-lg bg-[var(--surface)] border border-white/10 focus:border-[var(--accent)] outline-none text-sm w-full max-w-md" />
      <div className="overflow-x-auto rounded-2xl border border-white/10 bg-[var(--surface)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--surface-2)] border-b border-white/10">
            <tr><th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Name</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Type</th>
              <th className="text-left px-3 py-2 text-[10px] uppercase text-zinc-500">Country</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase text-zinc-500">Stints</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((t) => (
              <tr key={t.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 font-medium">{t.name}</td>
                <td className="px-3 py-2 text-zinc-400">{t.type?.replace("_"," ")}</td>
                <td className="px-3 py-2 text-zinc-400">{t.country}</td>
                <td className="px-3 py-2 text-right tabular-nums">{t.stint_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ────────────────────── Shell ────────────────────── */
const NAV = [
  { to: "/admin",         label: "Overview", icon: BarChart3,  end: true  },
  { to: "/admin/players", label: "Players",  icon: Users },
  { to: "/admin/teams",   label: "Teams",    icon: Shield },
  { to: "/admin/stints",  label: "Stints",   icon: Calendar },
  { to: "/admin/puzzles", label: "Puzzles",  icon: Layers },
];

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Admin only.");
      navigate("/dashboard", { replace: true });
    }
  }, [user, navigate]);
  if (!user) return null;
  if (user.role !== "admin") {
    return (
      <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] grid place-items-center">
        <div className="text-center" data-testid="admin-403">
          <AlertTriangle size={32} className="text-red-400 mx-auto mb-3" />
          <div className="text-2xl font-black">403 — admin only</div>
          <Link to="/dashboard" className="text-[var(--accent)] text-sm mt-3 inline-block">← back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]" data-testid="admin-shell">
      <header className="px-6 py-4 border-b border-white/5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-[var(--accent)]">← app</Link>
          <span className="text-xs uppercase tracking-[0.4em] text-[var(--accent)] font-bold">FootGuesser admin</span>
        </div>
        <span className="text-xs text-zinc-500">{user.email}</span>
      </header>
      <div className="grid grid-cols-[200px_1fr] min-h-[calc(100vh-57px)]">
        <aside className="border-r border-white/5 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm transition ${
                  isActive ? "bg-[var(--accent)]/10 text-[var(--accent)] font-bold" : "text-zinc-400 hover:bg-white/5"
                }`}>
              <n.icon size={14} /> {n.label}
            </NavLink>
          ))}
        </aside>
        <main className="p-6 overflow-x-hidden">
          <Routes>
            <Route index element={<Overview />} />
            <Route path="players" element={<PlayersTab />} />
            <Route path="teams" element={<TeamsTab />} />
            <Route path="stints" element={<StintsTab />} />
            <Route path="puzzles" element={<PuzzlesTab />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}
